import { Box } from 'vopay';
import { useState, useEffect } from 'react';
import { Title, SideDrawer, UtilButton } from 'UI';
import { useTranslation } from 'react-i18next';
import ApprovalSettingsContainer from '@/components/TransactionApproval/ApprovalSettings/ApprovalSettingsContainer';
import WorkflowContainer from '@/components/TransactionApproval/ApprovalSettings/Workflow/WorkflowContainer';
import WorkflowDetailsContainer from '@/components/TransactionApproval/ApprovalSettings/Workflow/WorkflowDetailsContainer';
import CreateWorkflowContainer from '@/components/TransactionApproval/ApprovalSettings/Workflow/UpdateWorkFlow/CreateWorkflowContainer';
import EditWorkflowContainer from '@/components/TransactionApproval/ApprovalSettings/Workflow/UpdateWorkFlow/EditWorkflowContainer';
import ApprovalRolesContainer from '@/components/TransactionApproval/ApprovalSettings/ApprovalRoles/ApprovalRolesContainer';

type View = 'settings' | 'workflows' | 'create' | 'details' | 'edit' | 'roles';

type WorkflowDrawerProps = {
  accountToken: string;
  accountId?: string;
  accountName: string;
  open: boolean;
  onClose: () => void;
};

export default function WorkflowDrawer({ accountToken, accountId, accountName, open, onClose }: WorkflowDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Workflow' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [view, setView] = useState<View>('settings');
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) setView('settings');
  }, [open]);

  const viewTitles: Record<View, string> = {
    settings: t('transactionApproval', { defaultValue: 'Transaction Approval' }),
    workflows: t('approvalWorkflow', { defaultValue: 'Approval Workflow' }),
    create: t('createWorkflow', { defaultValue: 'Create Workflow' }),
    details: t('workflowDetails', { defaultValue: 'Workflow Details' }),
    edit: t('editWorkflow', { defaultValue: 'Edit Workflow' }),
    roles: t('roles', { defaultValue: 'Roles' }),
  };

  const backView: Record<View, View> = {
    settings: 'settings',
    workflows: 'settings',
    create: 'workflows',
    details: 'workflows',
    edit: 'details',
    roles: 'settings',
  };

  return (
    <SideDrawer
      isOpen={open}
      onClose={onClose}
      title={
        <Box flx="col" gapSize="4px">
          {view !== 'settings' && (
            <UtilButton iconLeft="chevron_left" size="large" onClick={() => setView(backView[view])}>
              {tGlobal('Buttons.back', { defaultValue: 'Back' })}
            </UtilButton>
          )}
          <Title level="3">{viewTitles[view]}</Title>
          <div
            style={{
              fontSize: '15px',
              color: 'rgba(45, 45, 45, 0.75)',
              lineHeight: '20px',
              fontWeight: '400',
            }}
          >
            {accountName}
          </div>
        </Box>
      }
      style={{
        width: 'calc(100% - var(--navLeftWidthSmall, 80px))',
      }}
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      {view === 'settings' && (
        <ApprovalSettingsContainer
          subAccountToken={accountToken}
          onNavigateToWorkflows={() => setView('workflows')}
          onNavigateToWorkflowAdd={() => setView('create')}
          onNavigateToRoles={() => setView('roles')}
        />
      )}
      {view === 'workflows' && (
        <WorkflowContainer
          bearer={accountToken}
          accountId={accountId}
          onBack={() => setView('settings')}
          onCreateWorkflow={() => setView('create')}
          onViewWorkflow={(workflowId) => {
            setSelectedWorkflowId(workflowId);
            setView('details');
          }}
          onNavigateToRoles={() => setView('roles')}
        />
      )}
      {view === 'create' && (
        <CreateWorkflowContainer
          bearer={accountToken}
          accountId={accountId}
          onBack={() => setView('workflows')}
          onCreated={() => setView('workflows')}
          onNavigateToRoles={() => setView('roles')}
        />
      )}
      {view === 'details' && selectedWorkflowId && (
        <WorkflowDetailsContainer
          bearer={accountToken}
          workflowId={selectedWorkflowId}
          accountId={accountId}
          onBack={() => setView('workflows')}
          onEdit={() => setView('edit')}
        />
      )}
      {view === 'edit' && selectedWorkflowId && (
        <EditWorkflowContainer
          bearer={accountToken}
          workflowId={selectedWorkflowId}
          accountId={accountId}
          onBack={() => setView('details')}
          onEdited={() => setView('workflows')}
          onNavigateToRoles={() => setView('roles')}
        />
      )}
      {view === 'roles' && (
        <ApprovalRolesContainer bearer={accountToken} accountId={accountId} onBack={() => setView('settings')} />
      )}
    </SideDrawer>
  );
}
